import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import api from "../../../api/axiosInstance";
import { useCvStepNavigation } from "../../../contexts/CvStepNavigationContext";
import { usePatchCvMutation } from "../../../lib/cvsSlice";

// ── Date helpers ──────────────────────────────────────────────────────────────
const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

function toIsoDate(monthFinished, yearFinished) {
  if (!yearFinished) return "";
  const idx = MONTHS.indexOf(monthFinished);
  const month = idx >= 0 ? String(idx + 1).padStart(2, "0") : "01";
  return `${yearFinished}-${month}-01`;
}

function fromIsoDate(dateStr) {
  if (!dateStr) return { monthFinished: "", yearFinished: "" };
  const [year, month] = dateStr.split("-");
  return {
    monthFinished: MONTHS[parseInt(month, 10) - 1] ?? "",
    yearFinished: year ?? "",
  };
}

function formatDisplayDate(monthFinished, yearFinished) {
  if (!yearFinished) return "";
  const idx = MONTHS.indexOf(monthFinished);
  const month = idx >= 0 ? String(idx + 1).padStart(2, "0") : "01";
  return `01/${month}/${yearFinished}`;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const EMPTY_FORM = { name: "", completionDate: "", description: "" };
const MAX_FILE_SIZE_MB = 3;
const ALLOWED_TYPES = ["application/pdf", "image/png", "image/jpeg"];
const ALLOWED_LABEL = "PDF, PNG, JPG (max. 3MB)";

// ── File upload row ───────────────────────────────────────────────────────────
const FileRow = ({ file, onRemove }) => {
  const sizeKb = file.sizeKb ? `${file.sizeKb}kb` : "";
  const isFailed = file.status === "failed";
  const isComplete = file.status === "complete";
  const isLoading = file.status === "loading";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1,
        borderTop: "1px solid #E0DEDD",
      }}
    >
      <InsertDriveFileOutlinedIcon
        sx={{ fontSize: 20, color: isFailed ? "#E84610" : "#1976D2", flexShrink: 0 }}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {isFailed ? (
          <>
            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#E84610" }}>
              Upload failed.
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "#E84610" }}>
              File too large &bull; Failed
            </Typography>
          </>
        ) : (
          <>
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 500,
                color: "#1A1A1A",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {file.name}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "#7A706F" }}>
              {sizeKb} &bull; {isLoading ? "Loading" : "Complete"}
            </Typography>
          </>
        )}
      </Box>

      <IconButton size="small" onClick={() => onRemove(file.id)} sx={{ color: "#9E9E9E", "&:hover": { color: "#E84610" } }}>
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>

      <Box sx={{ width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {isLoading && (
          <CircularProgress
            variant="determinate"
            value={file.progress}
            size={24}
            thickness={5}
            sx={{ color: "#1976D2" }}
          />
        )}
        {isComplete && <CheckCircleIcon sx={{ color: "#4CAF50", fontSize: 24 }} />}
      </Box>
    </Box>
  );
};

// ── Upload dropzone ───────────────────────────────────────────────────────────
const UploadZone = ({ onFilesSelected }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    onFilesSelected(Array.from(e.dataTransfer.files));
  }, [onFilesSelected]);

  return (
    <Box>
      <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1A1A1A", mb: 1 }}>
        Upload Certificate
      </Typography>

      <Box
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        sx={{
          border: `2px dashed ${dragging ? "#1976D2" : "#C5BFBE"}`,
          borderRadius: "8px",
          p: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
          backgroundColor: dragging ? "#EEF4FF" : "#FAFAFA",
          transition: "all 0.2s",
          cursor: "pointer",
        }}
        onClick={() => inputRef.current?.click()}
      >
        <InsertDriveFileOutlinedIcon sx={{ fontSize: 32, color: "#9E9E9E" }} />
        <Typography sx={{ fontSize: "13px", color: "#3D3634" }}>
          <Box
            component="span"
            sx={{ color: "#3D3634", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
          >
            Select a file
          </Box>
          {" "}or drag and drop
        </Typography>
        <Typography sx={{ fontSize: "12px", color: "#9E9E9E" }}>{ALLOWED_LABEL}</Typography>
      </Box>

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg"
        multiple
        style={{ display: "none" }}
        onChange={(e) => onFilesSelected(Array.from(e.target.files))}
      />
    </Box>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const Courses = ({ courses, cvId }) => {
  const [localCourses, setLocalCourses] = useState(Array.isArray(courses) ? courses : []);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const isDirty = useRef(false);

  const [patchCv] = usePatchCvMutation();
  const { goToNextStep, goToPrevStep } = useCvStepNavigation();

  // Guard against 1-second CvDetails poll resetting local state
  useEffect(() => {
    if (isDirty.current) return;
    setLocalCourses(Array.isArray(courses) ? courses : []);
  }, [courses, cvId]);

  const setField = (key, value) => {
    isDirty.current = true;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setUploadedFiles([]);
  };

  // ── File upload ─────────────────────────────────────────────────────────────
  const handleFilesSelected = async (files) => {
    isDirty.current = true;
    for (const file of files) {
      const id = `${Date.now()}-${file.name}`;
      const sizeKb = Math.round(file.size / 1024);

      // Validate size and type
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024 || !ALLOWED_TYPES.includes(file.type)) {
        setUploadedFiles((prev) => [
          ...prev,
          { id, name: file.name, sizeKb, status: "failed", progress: 0 },
        ]);
        continue;
      }

      // Add as loading
      setUploadedFiles((prev) => [
        ...prev,
        { id, name: file.name, sizeKb, status: "loading", progress: 0 },
      ]);

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("fileType", "certifications/training");

        await api.post("/files", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadedFiles((prev) =>
              prev.map((f) => (f.id === id ? { ...f, progress: pct } : f))
            );
          },
        });

        setUploadedFiles((prev) =>
          prev.map((f) => (f.id === id ? { ...f, status: "complete", progress: 100 } : f))
        );
      } catch {
        setUploadedFiles((prev) =>
          prev.map((f) => (f.id === id ? { ...f, status: "failed" } : f))
        );
      }
    }
  };

  const removeFile = (id) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== id));
  };

  // ── Edit course ─────────────────────────────────────────────────────────────
  const handleEditRow = (course) => {
    isDirty.current = true;
    setEditingId(course._id);
    setForm({
      name: course.name ?? "",
      completionDate: toIsoDate(course.monthFinished, course.yearFinished),
      description: course.description ?? "",
    });
    setUploadedFiles([]);
  };

  // ── Delete course ───────────────────────────────────────────────────────────
  const handleDelete = async (courseId) => {
    isDirty.current = true;
    try {
      await patchCv({ cvId, courses: [{ id: courseId, delete: true }] }).unwrap();
      setLocalCourses((prev) => prev.filter((c) => c._id !== courseId));
      if (editingId === courseId) resetForm();
    } catch (err) {
      toast.error(`Failed to remove: ${err?.data?.message ?? err.message}`);
    }
  };

  // ── Exclude in CV toggle ────────────────────────────────────────────────────
  const handleExcludeToggle = async (course, newValue) => {
    isDirty.current = true;
    setLocalCourses((prev) =>
      prev.map((c) => (c._id === course._id ? { ...c, excludeInCv: newValue } : c))
    );
    try {
      await patchCv({ cvId, courses: [{ id: course._id, excludeInCv: newValue }] }).unwrap();
    } catch {
      setLocalCourses((prev) =>
        prev.map((c) => (c._id === course._id ? { ...c, excludeInCv: course.excludeInCv } : c))
      );
      toast.error("Failed to update exclude setting");
    }
  };

  // ── Unsaved-data guard for NEXT ─────────────────────────────────────────────
  const hasUnsavedData = () =>
    form.name.trim() !== "" ||
    form.completionDate !== "" ||
    form.description.trim() !== "" ||
    uploadedFiles.length > 0;

  const handleNext = () => {
    if (hasUnsavedData()) {
      setShowUnsavedWarning(true);
      return;
    }
    goToNextStep();
  };

  // ── Save course (add or update) ─────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Course name is required"); return; }
    if (!form.completionDate) { toast.error("Completion date is required"); return; }

    const { monthFinished, yearFinished } = fromIsoDate(form.completionDate);
    const payload = {
      name: form.name.trim(),
      monthFinished,
      yearFinished,
      description: form.description.trim(),
    };

    setIsSaving(true);
    try {
      const result = editingId
        ? await patchCv({ cvId, courses: [{ id: editingId, ...payload }] }).unwrap()
        : await patchCv({ cvId, courses: [payload] }).unwrap();

      setLocalCourses(result?.courses ?? localCourses);
      resetForm();
    } catch (err) {
      toast.error(`Failed to save: ${err?.data?.message ?? err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Existing course rows */}
      {localCourses.map((course) => (
        <Box
          key={course._id}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1.5,
            border: "1px solid #E0DEDD",
            borderRadius: "8px",
            backgroundColor: editingId === course._id ? "#FFF8F7" : "#fff",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1A1A1A", fontFamily: "Inter, sans-serif" }}>
              {course.name}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "#7A706F", fontFamily: "Inter, sans-serif" }}>
              {formatDisplayDate(course.monthFinished, course.yearFinished)}
            </Typography>
          </Box>

          <IconButton size="small" onClick={() => handleEditRow(course)} sx={{ color: "#9E9E9E", "&:hover": { color: "#1A1A1A" } }}>
            <EditOutlinedIcon fontSize="small" />
          </IconButton>

          <IconButton size="small" onClick={() => handleDelete(course._id)} sx={{ color: "#9E9E9E", "&:hover": { color: "#E84610" } }}>
            <CancelIcon fontSize="small" />
          </IconButton>

          <Tooltip title="Exclude this course from exported CV">
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
              <Switch
                size="small"
                checked={course.excludeInCv ?? false}
                onChange={(e) => handleExcludeToggle(course, e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#1A1A1A" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#1A1A1A" },
                }}
              />
              <Typography sx={{ fontSize: "11px", color: "#7A706F", whiteSpace: "nowrap" }}>
                Exclude in CV
              </Typography>
            </Box>
          </Tooltip>
        </Box>
      ))}

      {/* Inline form */}
      <TextField
        label="Course Name"
        placeholder="Enter Name"
        value={form.name}
        onChange={(e) => setField("name", e.target.value)}
        fullWidth
        variant="outlined"
        size="small"
        InputLabelProps={{ shrink: true }}
        autoComplete="off"
        inputProps={{ autoComplete: "off", name: "course-name" }}
      />

      <TextField
        label="Completion Date"
        type="date"
        value={form.completionDate}
        onChange={(e) => setField("completionDate", e.target.value)}
        fullWidth
        variant="outlined"
        size="small"
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        label="Description"
        placeholder="Enter description"
        value={form.description}
        onChange={(e) => setField("description", e.target.value)}
        fullWidth
        multiline
        rows={4}
        variant="outlined"
        InputLabelProps={{ shrink: true }}
      />

      {/* File upload */}
      <UploadZone onFilesSelected={handleFilesSelected} />

      {uploadedFiles.length > 0 && (
        <Box>
          {uploadedFiles.map((file) => (
            <FileRow key={file.id} file={file} onRemove={removeFile} />
          ))}
        </Box>
      )}

      {/* Save */}
      <Box>
        <Button
          variant="contained"
          data-tc="save"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "SAVE"}
        </Button>
      </Box>

      {showUnsavedWarning && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <InfoOutlinedIcon color="primary" fontSize="small" />
          <Typography data-tc="unsaved-warning">
            You have unsaved course details. Click SAVE before continuing, or they'll be lost.
          </Typography>
        </Box>
      )}

      {/* Navigation */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
        <Button
          variant="outlined"
          onClick={goToPrevStep}
          sx={{
            borderColor: "#C5BFBE",
            color: "#3D3634",
            borderRadius: "6px",
            px: 3,
            fontWeight: 600,
            letterSpacing: "0.5px",
            "&:hover": { borderColor: "#9E9E9E", backgroundColor: "#F4F2F1" },
          }}
        >
          BACK
        </Button>

        <Button
          variant="contained"
          onClick={handleNext}
          sx={{
            backgroundColor: "#E84610",
            "&:hover": { backgroundColor: "#C73A0D" },
            borderRadius: "6px",
            px: 3,
            fontWeight: 600,
            letterSpacing: "0.5px",
          }}
        >
          NEXT
        </Button>
      </Box>
    </Box>
  );
};

export default Courses;
